import { browser } from "$app/env";
import { createApiClient, createConsoleClient } from "@colossus/api-client";
import type {
  ApiClient,
  ApiFailure,
  ConsoleClient,
  Fetch,
  TenantPaths,
} from "@colossus/api-client";
import { demoFetch, inDemo } from "./demo/live.ts";
import { m } from "./paraglide/messages.js";
import { reader } from "./reader.ts";
import { signedInHere } from "./shell/me.ts";
import { toast } from "./ui/toast.svelte.ts";

type AppPath = keyof TenantPaths;

export type GetPath = {
  [P in AppPath]: TenantPaths[P] extends { get: object } ? P : never;
}[AppPath];

type Operation<P extends GetPath> = TenantPaths[P] extends { get: infer O } ? O : never;

export type Query<P extends GetPath> =
  Operation<P> extends { parameters: { query?: infer Q } } ? Extract<Q, object> : never;

export type Ok<P extends GetPath> =
  Operation<P> extends { responses: { 200: { content: { "application/json": infer R } } } }
    ? R
    : never;

const origin = (): string => reader().origin();

const demo = demoFetch(toast);

const watched =
  (fetchImpl: Fetch): Fetch =>
  async (input, init) => {
    if (inDemo(input)) return demo(input);
    const response = await fetchImpl(input, init);
    if (response.status === 401 && signedInHere()) {
      toast("critical", m.refusal_signed_out(), { signIn: true });
    }
    return response;
  };

const withinRequest = (fetchImpl: Fetch): Fetch =>
  browser || fetchImpl !== globalThis.fetch
    ? fetchImpl
    : (input, init) => reader().fetch(input, init);

export const appClient = (tenantId: string, fetchImpl: Fetch): ApiClient =>
  createApiClient({ baseUrl: origin(), tenantId, fetch: watched(withinRequest(fetchImpl)) });

export const consoleClient = (fetchImpl: Fetch): ConsoleClient => {
  const asked = withinRequest(fetchImpl);
  return createConsoleClient({
    baseUrl: origin(),
    fetch: watched((input, init) => asked(input, { ...init, credentials: "include" })),
  });
};

export class ApiError extends Error {
  readonly failure: ApiFailure;

  constructor(path: string, failure: ApiFailure) {
    super(`${path} answered ${failure.status}`);
    this.name = "ApiError";
    this.failure = failure;
  }
}

type ImagePath = {
  [P in AppPath]: TenantPaths[P] extends {
    get: { responses: { 200: { content: { "image/webp": unknown } | { "image/png": unknown } } } };
  }
    ? P
    : never;
}[AppPath];

const PARAMETER = /\{(\w+)\}/g;

const imageUrl = (
  base: string,
  path: string,
  params: Readonly<Record<string, string>>,
  query: Readonly<Record<string, string>>,
): string => {
  const expanded = path.replaceAll(PARAMETER, (_match, name: string) => {
    const value = params[name];
    if (value === undefined) throw new Error(`${path} needs a ${name} path parameter`);
    return encodeURIComponent(value);
  });
  const url = new URL(expanded, base);
  for (const [name, value] of Object.entries(query)) url.searchParams.set(name, value);
  return url.toString();
};

const MEMBER_SEARCH = "/t/{tenantId}/api/analytics/members/search";

export const searchMembers = async (
  client: ApiClient,
  guildId: string,
  q: string,
): Promise<readonly { id: string; name: string; avatar: string | null; deleted: boolean }[]> => {
  const result = await client.call("get", MEMBER_SEARCH, { query: { guildId, q } });
  if (!result.ok) throw new ApiError(MEMBER_SEARCH, result);
  return result.data.map((row) => ({
    id: row.id,
    name: row.name,
    avatar: row.avatar,
    deleted: row.deleted,
  }));
};

type DownloadPath = {
  [P in AppPath]: TenantPaths[P] extends {
    get: {
      responses: {
        200: { content: { "text/csv": unknown } | { "application/octet-stream": unknown } };
      };
    };
  }
    ? P
    : never;
}[AppPath];

export const appDownload = (
  tenantId: string,
  path: DownloadPath,
  query: Readonly<Record<string, string>>,
  params: Readonly<Record<string, string>> = {},
): string => imageUrl(origin(), path, { ...params, tenantId }, query);

export const appImage = (
  tenantId: string,
  path: ImagePath,
  params: Readonly<Record<string, string>>,
  query: Readonly<Record<string, string>>,
): string => imageUrl(origin(), path, { ...params, tenantId }, query);

type ConsoleImagePath =
  | "/api/members/{guildId}/card/pictures/{assetId}/image"
  | "/api/members/{guildId}/card/server-pictures/{assetId}/image"
  | "/api/members/{guildId}/role/icons/{assetId}/image";

export const consoleImage = (
  path: ConsoleImagePath,
  params: Readonly<Record<string, string>>,
): string => imageUrl(origin(), path, params, {});
